import clsx, { ClassValue } from 'clsx'
import { type ReactNode, type SelectHTMLAttributes } from 'react'
import { useTranslation } from 'react-i18next'

export const SelectSizes = ['lg', 'md', 'sm', 'xs'] as const

export type SelectSize = typeof SelectSizes[number]

export type SelectProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> & {
  options: { value: string, label: ReactNode, disabled?: boolean }[]
  label?: ReactNode
  size?: SelectSize
  color?: 'warning' | 'error'
  error?: boolean
  helperText?: ReactNode
  className?: ClassValue
  isClearable?: boolean
}

export function Select (props: SelectProps) {
  const { label, options, size = 'md', color, error, helperText, className, isClearable, ...selectProps } = props

  const { t } = useTranslation()

  return (
    <label className="fieldset w-full">
      {label && (
        <legend className="pb-1">
          <span
            className={clsx(
              'text-sm',
              error && 'text-error'
            )}
          >
            {label}
          </span>
        </legend>
      )}
      <select
        className={clsx(
          'select',
          'w-full',
          'rounded-lg',
          'text-sm',
          error && 'select-error',
          color === 'warning' && 'select-warning',
          color === 'error' && 'select-error',
          size === 'lg' && 'select-lg',
          size === 'md' && ['select-md', 'h-8'],
          size === 'sm' && ['select-sm'],
          size === 'xs' && 'select-xs',
          className
        )}
        {...selectProps}
      >
        {isClearable && <option key={undefined} value={undefined}> {t('All')}</option>}
        {options.map(option => (
          <option
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </option>
        ))}
      </select>
      {helperText !== undefined && (
        <div className="label py-1">
          <span
            className={clsx(
              'min-h-5',
              error && 'text-error'
            )}
          >
            {helperText}
          </span>
        </div>
      )}
    </label>
  )
}
